import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../Button/Button';
import { Image } from 'shared/types';
import { getEpisodeLabel } from 'shared/utils/getEpisodeLabel';
import { CategoryTag } from 'shared/data/episodes';

export interface EpisodeCardProps {
  image: Image;
  category: CategoryTag;
  number: string;
  title: string;
  description: string;
  onPlay?: () => void;
}

export function EpisodeCard({
  image,
  category,
  number,
  title,
  description,
  onPlay,
}: EpisodeCardProps) {
  const { t } = useTranslation();
  return (
    <article
      className="group relative flex flex-col bg-surface-container-low rounded-xl overflow-hidden transform-gpu transition-transform duration-300 hover:-translate-y-1"
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={image.src}
          alt={image.alt}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        {category && <div className="absolute top-4 left-4 bg-surface-container-highest/60 backdrop-blur-md px-3 py-1 rounded-full">
          <span className="text-tertiary font-sans text-[10px] font-bold tracking-widest uppercase">
            {Array.isArray(category) ? category.join(', ') : category}
          </span>
        </div>}
      </div>

      {/* Content */}
      <div className="p-8 flex flex-col flex-grow">
        <div className="flex items-center justify-between mb-4">
          <span className="text-on-surface-variant font-sans text-xs uppercase tracking-tighter">
            {getEpisodeLabel(number)}
          </span>
          {/* <span className="text-on-surface-variant font-sans text-xs uppercase tracking-tighter">
            {duration}
          </span> */}
        </div>

        <h3 className="text-2xl font-serif font-bold text-on-surface mb-3 group-hover:text-tertiary transition-colors">
          <Link to={`/episodes/${number}`} className="after:absolute after:inset-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tertiary focus-visible:ring-offset-2 focus-visible:rounded-xl">
            {title}
          </Link>
        </h3>

        <p className="text-on-surface-variant text-sm font-light leading-relaxed mb-8">
          {description}
        </p>

        {/* Actions */}
        <div className="mt-auto pt-6 flex items-center justify-between border-t border-outline-variant/10">
          {onPlay ? (
            <Button
              variant="link"
              size="sm"
              icon="play-circle"
              iconWeight="fill"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPlay();
              }}
              className="relative z-10"
            >
              {t('listenNow')}
            </Button>
          ) : (
            <Button
              variant="link"
              size="sm"
              icon="play-circle"
              iconWeight="fill"
              to={`/episodes/${number}`}
              className="relative z-10"
            >
              {t('listenNow')}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
